import type {
  Filter,
  FilterDefinition,
} from "@/shared/components/ResourceView";
import { MAX_METRIC_GROUPS } from "@/shared/domain/metrics/MetricRecord";

/**
 * The filters that select exactly the given series of a grouped chart: the
 * rows whose group-by field holds one of `keys`, where a `null` key is the
 * series of rows with no value for the field.
 *
 * Expressible as ordinary {@link Filter}s because every group-by path is, by
 * construction, one of the dashboard's filter fields (see
 * `groupableFieldDefinitions`). That is what lets the drill-down show the
 * clicked bucket's series as chips the reader can edit or remove like any
 * other.
 *
 * The keys always make one `ANY_OF` chip, in sorted order, whatever the
 * field's type. A `null` key joins the same chip as its "Not set" member
 * (`includeNull`), so the filters AND with any the reader adds afterwards.
 *
 * At most {@link MAX_METRIC_GROUPS} named keys make the chip — the first, in
 * sorted order — with Not set on top, as the chart draws them. A chart never
 * reports more, since the server caps its groups; trimming rather than
 * refusing keeps a drill-down usable if a caller ever passes more, at the cost
 * of the chip naming fewer series than were asked for.
 *
 * `keys` arrive as the backend's text cast of each value, so a boolean field's
 * keys are `"true"` / `"false"`. Returns `undefined` when there is nothing to
 * match or a key or field type no group-by can produce, leaving the caller to
 * drill in unfiltered.
 */
export function groupKeysToFilters(
  definition: FilterDefinition,
  keys: readonly (string | null)[],
): Filter[] | undefined {
  // Sorted, so the chip reads in the legend's order rather than in whatever
  // order the click happened to report its points.
  const present = [
    ...new Set(keys.filter((key): key is string => key !== null)),
  ]
    .sort()
    .slice(0, MAX_METRIC_GROUPS);
  const includesNull = keys.includes(null);
  if (present.length === 0 && !includesNull) {
    return undefined;
  }
  const notSet = includesNull ? { includeNull: true } : {};
  const { field } = definition;

  switch (definition.type) {
    case "string":
      return [
        {
          type: "string",
          field,
          comparator: "ANY_OF",
          values: present,
          ...notSet,
        },
      ];
    case "enum":
      return [
        {
          type: "enum",
          field,
          comparator: "ANY_OF",
          values: present.map((key) => ({ value: key, label: key })),
          ...notSet,
        },
      ];
    case "boolean":
      if (present.some((key) => key !== "true" && key !== "false")) {
        return undefined;
      }
      return [
        {
          type: "boolean",
          field,
          comparator: "ANY_OF",
          values: present.map((key) => key === "true"),
          ...notSet,
        },
      ];
    default:
      return undefined;
  }
}
